import React, { useCallback, useMemo } from 'react'
import { ReservationF } from 'src/app/pages/booking/reservation-detail/types/reservation-detail.types'
import { ListSubheader, MenuItem, Select as VaporSelect } from '@vapor/material'
import { Divider, Select as MUISelect } from '@mui/material'
import { ReservedResourceOption, useReservedResourceOptions } from './reserved-resource-options'
import { useTranslation } from 'react-i18next'
import { ReferenceCustomerFragment } from 'src/api/api.generated'
import { NOT_SELECTED } from 'src/app/pages/booking/reservation-detail/reserved-resource-filter/reserved-resource-filter'
const HicSelect = VaporSelect as unknown as typeof MUISelect

type ReservedResourceWithCustomersSelectProps = {
  reservation: ReservationF
  value: string
  onSelectChange?: (guestId: string, reservedResourceId?: string | null) => void
}

type ReservedResourceWithCustomersOption = {
  grOption: ReservedResourceOption
  guestsOptions: {
    id: string
    description: string
  }[]
}

export const ReservedResourceWithCustomersSelect = (props: ReservedResourceWithCustomersSelectProps) => {
  const { t } = useTranslation()
  const options: ReservedResourceOption[] = props.reservation ? useReservedResourceOptions(props.reservation) : []

  const parsedReservationReferences = useMemo(() => {
    return props.reservation.customerReferenceList.reduce((prev: ReferenceCustomerFragment[], current: ReferenceCustomerFragment): ReferenceCustomerFragment[] => {
      if (prev?.findIndex((x) => x.id === current.id) >= 0) return [...prev]
      else return [...prev, current]
    }, [])
  }, [props.reservation])

  const groupedOptions = useMemo(() => {
    const reservedResources = props.reservation.reservedResources
    const result = options.map<ReservedResourceWithCustomersOption>((o) => {
      const rrOption = reservedResources?.find((rr) => rr.id === o.id)
      if (rrOption) {
        return { grOption: o, guestsOptions: rrOption.guestOccupancies?.map((g) => ({ id: g.guest.id, description: g.guest.name })) ?? [] }
      } else {
        if (!('index' in o)) {
          return {
            grOption: { ...o },
            guestsOptions: parsedReservationReferences.map((c) => ({
              id: c.id,
              description: c.name,
            })),
          }
        } else {
          return { grOption: { ...o }, guestsOptions: [] }
        }
      }
    })
    return result
  }, [options, props.reservation, parsedReservationReferences])

  const selectOptions = useMemo(() => {
    return groupedOptions.map((option) => {
      const { grOption, guestsOptions } = option
      const groupElement = (
        <ListSubheader key={grOption.id}>
          {'index' in grOption ? `${grOption.index} ${grOption.resourceType || ''} (${grOption.resourceCode || '-'})` : t('pms.booking.reservation.reserved_resource_not_selected')}
        </ListSubheader>
      )

      const subItems = guestsOptions.map((g) => {
        const value = `${grOption.id}#${g.id}`
        return (
          <MenuItem key={g.id} value={value}>
            {g.description}
          </MenuItem>
        )
      }) || <Divider light />

      return [groupElement, subItems]
    })
  }, [groupedOptions])

  const onChangeHandler = useCallback(
    (selectedValue: string) => {
      const keys = selectedValue.split('#')
      if (keys.length === 2) {
        const reservationId = keys[0] !== NOT_SELECTED ? keys[0] : null
        props.onSelectChange?.(keys[1], reservationId)
      }
    },
    [props.onSelectChange],
  )

  return (
    <HicSelect<string>
      value={props.value}
      onChange={(v) => {
        onChangeHandler(v.target.value)
      }}
    >
      {selectOptions}
    </HicSelect>
  )
}
